import { useEffect, useState, useRef } from 'react';
import AnalysisChart from './AnalysisChart';
import { normalizeAccount } from "../../utils/normalizeAccount";
import AnalyticsDetailsPanel from "./AnalyticsDetailsPanel";
import SummaryBar from "./SummaryBar";

export default function AnalysisChartProducts({
  account,
  marketplace,
  start,
  end,
  asins,
  onViewDetails
}) {
  const [chartData, setChartData] = useState(null);
  const sortedAsins = [...asins].sort().join(',');

  const cacheKey = `analytics:${account}:${marketplace}:${start || "default"}:${end || "default"}:${sortedAsins}`;

  const normalizedAccount = normalizeAccount(account);

  const params = {
    account: normalizedAccount,
    marketplace,
    asins,
    ...(start ? { start } : {}),
    ...(end ? { end } : {}),
  };
  
  const [detailsPanel, setDetailsPanel] = useState({
    open: false,
    monthIndex: null,
    monthLabel: null,
  });

  const openDetailsPanel = ({ monthIndex, monthLabel }) => {
    setDetailsPanel({
      open: true,
      monthIndex,
      monthLabel,
    });
  };

  const isSingleAsin = asins?.length === 1;
  const isMobile = window.innerWidth < 768;
  const MARK_AREA_HALF_WIDTH = isMobile ? 0.35 : 0.45;

  const hasSessionsStack = true; // або визначай по series
  const COL_WIDTH_MOBILE = hasSessionsStack ? 44 : 28;

  const SALES_BAR_MIN = isMobile ? 18 : undefined;
  const SALES_BAR_MAX = isMobile ? 26 : undefined;

  const SESSIONS_BAR_MIN = isMobile ? 10 : undefined;
  const SESSIONS_BAR_MAX = isMobile ? 16 : 20;

  useEffect(() => {
    window.__openDetailsPanel = (monthIndex, monthLabel) => {
      openDetailsPanel({ monthIndex, monthLabel });
    };
  }, [openDetailsPanel]);

  function buildOption(data) {
    const timelineIndexMap = {};
    (data.timeline || []).forEach((label, idx) => {
      timelineIndexMap[label] = idx;
    });

    let tooltipExpanded = false;
    let showIssues = true;
    let showChanges = true;

    const totalSales = data.series.sales || [];
    const adSales = data.series["ad-sales"] || [];
    const otherSales = data.series["other-sales"] || [];
    const organicSales = totalSales.map((total, i) =>
      Math.max(total - (adSales[i] || 0), 0)
    );
    // TOTAL = sales + other-sales
    const totalSalesAll = totalSales.map((total, i) =>
      total + (otherSales[i] || 0)
    );

    const totalSessions = data.series.sessions || [];
    const adClicks = data.series["ad-clicks"] || [];
    const organicSessions = totalSessions.map((total, i) =>
      Math.max(total - (adClicks[i] || 0), 0)
    );

    const refunds = (data.series.refunds || []).map(v => -Math.abs(v));

    const bsr = data.series.bsr || [];

    const markerMonths = isSingleAsin
      ? Array.from(
          new Set([
            ...Object.keys(data.changes || {}),
            ...Object.keys(data.oos || {}),
            ...Object.keys(data.hijackers || {}),
          ])
        )
      : [];

      const markerPoints = markerMonths
        .map((label) => {
          const xIndex = timelineIndexMap[label];
          if (xIndex === undefined) return null;

          const items = data.changes?.[label] || [];
          const hasIssue = !!(data.oos?.[label] || data.hijackers?.[label]);

          return {
            coord: [xIndex, 0],
            labelKey: label,
            items,
            hasIssue,
          };
        })
        .filter(Boolean);

      const outOfStockAreas = isSingleAsin
        ? Object.entries(data.oos || {})
            .map(([label, info]) => {
              const xIndex = timelineIndexMap[label];
              if (xIndex === undefined) return null;

              return [
                {
                  xAxis: xIndex - MARK_AREA_HALF_WIDTH,
                  itemStyle: {
                    color: "rgba(200,200,200,0.25)",
                  },
                  label: {
                      show: false, // ⬅️ важливо
                  },                },
                {
                  xAxis: xIndex + MARK_AREA_HALF_WIDTH,
                },
              ];
            })
            .filter(Boolean)
        : [];

        const hijackerAreas = isSingleAsin
          ? Object.entries(data.hijackers || {})
              .map(([label, info]) => {
                const xIndex = timelineIndexMap[label];
                if (xIndex === undefined) return null;

                return [
                  {
                    xAxis: xIndex- MARK_AREA_HALF_WIDTH,
                    itemStyle: {
                      color: "rgba(255,180,0,0.25)",
                    },
                    label: {
                        show: false, // ⬅️ важливо
                    },                  },
                  {
                    xAxis: xIndex+ MARK_AREA_HALF_WIDTH,
                  },
                ];
              })
              .filter(Boolean)
          : [];


    const yAxis = [
           {
             type: "value",
             name: "Sales",
             offset: 40,
             position: "left",
             scale: true,
             splitLine: { lineStyle: { type: "dashed" } },
             max: (value) => Math.ceil(value.max * 1.1),
             show: !isMobile,
             axisLabel: { show: !isMobile },
             axisTick: { show: !isMobile },
             axisLine: { show: !isMobile }
           },
           {
             type: "value",
             name: "Amount",
             position: "left",
             show: !isMobile,
             axisLabel: { show: !isMobile },
             axisTick: { show: !isMobile },
             axisLine: { show: !isMobile }
           },
           {
             type: "value",
             splitLine: { // Disable horizontal grid lines
                show: false
              },
             name: "Rates (%)",
             inverse: true,
             position: "right",
           //  offset: 60,
             axisLabel: {
               formatter: "{value} %",
             },
             show: !isMobile,
             axisLabel: { show: !isMobile },
             axisTick: { show: !isMobile },
             axisLine: { show: !isMobile }
           },
           {
             type: "value",
             splitLine: { // Disable horizontal grid lines
                show: false
              },
             name: "Sessions",
             position: "right",
             show: false,
             axisLabel: { show: false },
             axisTick: { show: false },
             axisLine: { show: false }
           }
         ];

     if (isSingleAsin) {
           yAxis.push({
             type: "value",
             splitLine: { // Disable horizontal grid lines
                show: false
              },
             name: "BSR",
             inverse: true,          // ⬅️ КЛЮЧОВЕ
             scale: true,
             position: "right",
             show: false,
             axisLabel: { show: false },
             axisTick: { show: false },
             axisLine: { show: false }
           });
         }

     const issueMarkerSeriesName = '__Issues__';
     const changeMarkerSeriesName = '__Changes__';

     const emphasisSeries = {
       emphasis: {
         focus: 'series'
       },
       blur: {
         itemStyle: {
           opacity: 0.5
         }
       }
     };

    const series =[
      ...(isSingleAsin
          ? [
              {
                name: issueMarkerSeriesName,
                type: 'scatter',
                symbol: 'triangle',
                symbolSize: 12,
                itemStyle: { color: '#EE6666' },
                data: [],
                silent: true,
              },
              {
                name: changeMarkerSeriesName,
                type: 'scatter',
                symbol: 'triangle',
                symbolSize: 12,
                itemStyle: { color: '#EFBF04' },
                data: [],
                silent: true,
              },
            ]
          : []),      {
        name: "__annotations__",
        type: "bar",
        stack: "sales",          // ⬅️ КЛЮЧОВЕ
        yAxisIndex: 0,
        xAxisIndex: 0,
        data: data.timeline.map(() => 0),
        silent: true,
        itemStyle: { opacity: 0 },
        emphasis: { disabled: true },
        tooltip: { show: false },
        showInLegend: false,
        markArea: {
          silent: true,
          data: [
            ...outOfStockAreas,
            ...hijackerAreas,
          ],
        },
      },
      {
        name: "Ad sales",
        type: "bar",
        stack: "sales",
        yAxisIndex: 0,
        data: adSales,
        itemStyle: {
          color: "#3BA272", // темно-зелений
        },
        barMinWidth: SALES_BAR_MIN,
        barMaxWidth: SALES_BAR_MAX,
        //barGap: '30%',
        barGap: '20%',
        barCategoryGap: isMobile ? '20%' : '30%',
        ...emphasisSeries
      },
      {
        name: "Other sales",
        type: "bar",
        stack: "sales",
        yAxisIndex: 0,
        data: otherSales,
        itemStyle: {
          color: "#C5E1A5",
        },
        barMinWidth: SALES_BAR_MIN,
        barMaxWidth: SALES_BAR_MAX,
        barGap: '20%',
        barCategoryGap: isMobile ? '20%' : '30%',
        ...emphasisSeries
      },
      {
        name: "Organic sales",
        type: "bar",
        stack: "sales",
        yAxisIndex: 0,
        data: organicSales,
        itemStyle: {
          color: "#91CC75", // світло-зелений
        },
        barMinWidth: SALES_BAR_MIN,
        barMaxWidth: SALES_BAR_MAX,
        barGap: '20%',
        barCategoryGap: isMobile ? '20%' : '30%',
        label: {
          show: true,
          position: "insideTop",
          color: "#ffffff",
          fontSize: 11,
          fontWeight: 600,
          formatter: ({ dataIndex }) => {
            const total = totalSalesAll[dataIndex];
            return total ? total.toLocaleString() : "";
          },
        },
        ...emphasisSeries
      },
      {
        name: "Refunds",
        type: "bar",
        stack: "sales",
        yAxisIndex: 0,
        data: refunds,
        itemStyle: {
          color: "#D94C4C", // червоний (default echarts red)
        },
        barMinWidth: SALES_BAR_MIN,
        barMaxWidth: SALES_BAR_MAX,
        barGap: '20%',
        barCategoryGap: isMobile ? '20%' : '30%',
        ...emphasisSeries
        /*label: {
          show: true,                 // ⬅️ поки вмикаємо
          position: "bottom",         // ⬅️ під нульовою лінією
          color: "#EE6666",
          fontSize: 11,
          formatter: ({ value }) =>
            value ? Math.abs(value).toLocaleString() : "",
        }, */
      },
      {
        name: "Ad clicks",
        type: "bar",
        stack: "sessions",
        yAxisIndex: 3,
        data: data.series['ad-clicks'],
        itemStyle: {
          color: "#309EC9",
        },
        barMinWidth: SESSIONS_BAR_MIN,
        barMaxWidth: SESSIONS_BAR_MAX,
        barGap: '20%',
        barCategoryGap: isMobile ? '20%' : '30%',
        ...emphasisSeries
      },
      {
        name: "Organic sessions",
        type: "bar",
        stack: "sessions",
        yAxisIndex: 3,
        data: organicSessions,
        itemStyle: {
          color: "#73C0DE",
        },
        barMinWidth: SESSIONS_BAR_MIN,
        barMaxWidth: SESSIONS_BAR_MAX,
        barGap: '20%',
        barCategoryGap: isMobile ? '20%' : '30%',
        ...emphasisSeries
      },
      {
        name: "Profit",
        type: "line",
        smooth: 0.4,
        yAxisIndex: 1,
        data: data.series.profit,
        itemStyle: {
          color: "#000000",
        },
        label: {
            show: true,
            position: "top",
            fontSize: 11,
            color: "#000000",
            formatter: ({ value }) =>
              value ? Math.ceil(value).toLocaleString() : "",
          },
          emphasis: {
              focus: 'series',
              lineStyle: { width: 3 }
            },
          blur: {
              lineStyle: { opacity: 0.5 }
            }
      },
      {
        name: "Ad ACoS",
        type: "line",
        smooth: 0.4,
        yAxisIndex: 2,
        data: data.series["ad-acos"],
        itemStyle: {
          color: "#FC8452",
        },
        emphasis: {
            focus: 'series',
            lineStyle: { width: 3 }
        },
        blur: {
            lineStyle: { opacity: 0.5 }
        },
        markPoint: {
          symbol: "triangle",
          symbolSize: 12,
          data: markerPoints
          .filter(p =>
            (p.hasIssue && showIssues) ||
            (!p.hasIssue && showChanges)
          )
          .map((p) => ({
            coord: p.coord,
            items: p.items,
            labelKey: p.labelKey,
            itemStyle: {
              color:  p.hasIssue
                ? "#EE6666"
                : "#EFBF04",
            },
            emphasis: {
              itemStyle: {
                color: p.hasIssue
                ? "#D94C4C"
                : "#D9A400",
              },
            },
          })),
          tooltip: {
            trigger: "item",
            renderMode: "html",
            className: "echarts-dark-tooltip",
            formatter: (params) => {
              const label = params.data.labelKey;
              const items = params.data.items || [];

              const oos = data.oos?.[label];
              const hijacker = data.hijackers?.[label];

              let html = ``;

              if (items.length) {
                html += `<strong class='tooltip-changes'>Changes (${items.length})</strong>`;
                html += `<br/>` + items
                  .map((c) => `• <strong>${c.type}</strong>: ${c.description}`)
                  .join("<br/>");
                html += `<br/>`
              }

              if (oos || hijacker) {
                html += `<strong class='tooltip-issue'>Issues</strong>`;
                if (oos?.days)
                  html += `<br/>• Out of stock: <span class='tooltip-issue'>${oos.days} day(s)</span>`;
                if (hijacker?.days)
                  html += `<br/>• Hijacker: <span class='tooltip-issue'>${hijacker.days} day(s)</span>`;
              }

              return html;
            },
          },
        },
      },
    ];

    if (isSingleAsin) {
      series.push(
        {
          name: "BSR",
          type: "line",
          yAxisIndex: 4,
          data: bsr,
          showSymbol: false,
          smooth: 0.4,
          itemStyle: {
            color: "#5470C6", // muted blue (echarts default)
          },
          lineStyle: {
            width: 2,
            type: "dashed",   // optional, щоб не конкурувала
          },
          label: {
            show: false,
          },
        }
      );
    }

    const tooltipFormatter= (params) => {
        const index = params[0].dataIndex;
        window.__lastTooltipIndex = index;
        const title = params[0].axisValue;

        const sales = data.series.sales?.[index] || 0;
        const ad = data.series["ad-sales"]?.[index] || 0;
        const other = data.series["other-sales"]?.[index] || 0;
        const organic = Math.max(sales - ad, 0);
        const total = sales + other;

        const refunds = data.series.refunds?.[index] || 0;
        const profit = data.series.profit?.[index];
        const acos = data.series["ad-acos"]?.[index];
        const bsr = isSingleAsin ? data.series.bsr?.[index] : null;

        const sess = data.series.sessions?.[index];
        const adcls = data.series["ad-clicks"]?.[index];

        let html = `<strong>${title}</strong><br/>`;

        // ===== SUMMARY (always visible) =====
        html += `<span class="tooltip-sales"><strong>Total sales:</strong> ${total.toLocaleString()}</span><br/>`;
        html += `Organic/Ad/Other   <span class="tooltip-sales">`;
        html += organic ? organic.toLocaleString()+'/' : '-/';
        html += ad ? ad.toLocaleString()+'/' : '-/';
        html += other ? other.toLocaleString() : '';
        html += `</span>`;

        if(refunds)
          html += `<br/><span class="tooltip-refunds"><strong>Refunds:</strong> ${total.toLocaleString()}</span>`;

        if (profit != null) {
          html += `<br/><span class="tooltip-profit"><strong>Profit:</strong> $${Math.ceil(
            profit
          ).toLocaleString()}</span>`;
        }

        if(sess)
          html += `<br/><span class="tooltip-bsr"><strong>Sessions:</strong> ${sess.toLocaleString()}</span>`;
        if(adcls)
            html += `<br/><span class="tooltip-bsr"><strong>Ad clicks:</strong> ${adcls.toLocaleString()}</span>`;

        if (acos != null) {
          html += `<br/><span class="tooltip-acos"><strong>Ad ACoS:</strong> ${acos.toFixed(
            1
          )}%</span>`;
        }

        if (bsr != null) {
          html += `<br/><span class="tooltip-bsr"><strong>BSR:</strong> ${Math.ceil(
            bsr
          ).toLocaleString()}</span>`;
        }


        // ===== TOGGLE BUTTON =====
        html += `<br/>
          <div style="margin-top:3px;">
          <a
                href="#"
                style="color:#9CDCFE; font-size:11px; text-decoration:underline;"
                onclick="event.preventDefault(); window.__openDetailsPanel(${index}, '${title}')"
              >
                View details
              </a>
          </div>
        `;

        return html;
    }


    return {
      tooltip: {
        trigger: "axis",
        renderMode: "html",
        className: "echarts-dark-tooltip",
        textStyle: {
            fontSize: 12,
            //fontWeight: 'no'
        },
        axisPointer: { type: "shadow" },
        enterable: true,
        formatter: tooltipFormatter,
      },
      legend: {
        type: "scroll",
        top: 5,
        left: "center",
        data: [
            'Ad sales',
            'Other sales',
            'Organic sales',
            'Refunds',
            'Organic sessions',
            'Ad clicks',
            'Profit',
            'Ad ACoS',
            ...(isSingleAsin ? [issueMarkerSeriesName, changeMarkerSeriesName] : []),
            ...(isSingleAsin ? ['BSR'] : []),
          ],
          formatter: (name) => {
            if (name === issueMarkerSeriesName) return 'Issues';
            if (name === changeMarkerSeriesName) return 'Changes';
            return name;
          },
          selected: {
            ...(isSingleAsin
              ? {
                  [issueMarkerSeriesName]: true,
                  [changeMarkerSeriesName]: true,
                  BSR: false,
                }
              : {}),
            ...(isMobile
              ? {
                  'Organic sessions': false,
                  'Ad clicks': false,
                }
              : {}),
          },
      },
      grid: {
        left: isMobile ? 6 : 40,
        right: isMobile ? 6 : 20,
        top: 50,
        bottom: 70,
      },
      xAxis: {
        type: "category",
        data: data.timeline
      },
      yAxis: yAxis,
      splitLine: {
        show: !isMobile
      },
      dataZoom: [
        { type: "slider",
          bottom: 5,
          fillerColor:'rgba(109,154,254,0.2)'
          //zoomLock: true
        }
      ],
      series: series
    };
  }

  return (
    <>
      {chartData && (
        <AnalyticsDetailsPanel
          show={detailsPanel.open}
          onClose={() => setDetailsPanel({ open: false })}
          data={chartData}
          monthIndex={detailsPanel.monthIndex}
          monthLabel={detailsPanel.monthLabel}
        />
      )}

      {chartData?.totals && (
        <SummaryBar
          primary={chartData.totals.primary}
          secondary={chartData.totals.secondary}
          isSingleAsin={isSingleAsin}
        />
      )}

      <div className="chart-scroll-wrapper">
        <div className="chart-scroll">
          <div
            className="chart-inner"
            style={{
              minWidth: chartData
                ? chartData.timeline.length * COL_WIDTH_MOBILE
                : '100%',
            }}
          >
            <AnalysisChart
              cacheKey={cacheKey}
              endpoint="/amz-api/products/charts/analysis"
              params={params}
              buildOption={buildOption}
              onViewDetails={openDetailsPanel}
              onDataLoaded={setChartData}
            />
          </div>
        </div>
        <div className="chart-scroll-fade" />
      </div>

    </>
  );
}
